import { queryOptions } from "@tanstack/react-query";
import type { InferRequestType, InferResponseType } from "hono/client";
import { api, unwrap } from "@/client/lib/api";
import type {
  AccountSettings,
  NotificationSetting,
  Preferences,
  ProfileSettings,
  SignedInSession,
} from "@/features/settings/schemas";

export const profileQuery = queryOptions({
  queryKey: ["settings", "profile"],
  queryFn: (): Promise<ProfileSettings> => unwrap(api.settings.profile.$get()),
});

export const accountQuery = queryOptions({
  queryKey: ["settings", "account"],
  queryFn: (): Promise<AccountSettings> => unwrap(api.settings.account.$get()),
});

/**
 * Not on the session snapshot: every guard reads the snapshot, and a date format is not worth
 * emptying the cache to change.
 */
export const preferencesQuery = queryOptions({
  queryKey: ["settings", "preferences"],
  queryFn: (): Promise<Preferences> => unwrap(api.settings.preferences.$get()),
});

export const notificationsQuery = queryOptions({
  queryKey: ["settings", "notifications"],
  queryFn: (): Promise<NotificationSetting[]> =>
    unwrap(api.settings.notifications.$get()),
});

export const sessionsQuery = queryOptions({
  queryKey: ["settings", "sessions"],
  queryFn: (): Promise<SignedInSession[]> =>
    unwrap(api.settings.sessions.$get()),
});

export type CompanyShown = InferResponseType<typeof api.company.$get, 200>;
export type CompanyChange = InferRequestType<typeof api.company.$patch>["json"];

export const companyQuery = queryOptions({
  queryKey: ["company"],
  queryFn: (): Promise<CompanyShown> => unwrap(api.company.$get()),
});

export type ExportShown = InferResponseType<
  typeof api.company.export.$get,
  200
>["latest"];

export const exportQuery = queryOptions({
  queryKey: ["company", "export"],
  queryFn: (): Promise<{ latest: ExportShown }> =>
    unwrap(api.company.export.$get()),
  // Asked again while one is being prepared, so the line moves on without a reload.
  refetchInterval: (query) => {
    const status = query.state.data?.latest?.status;
    return status === "queued" || status === "building" ? 30_000 : false;
  },
});
